import { useEffect } from 'react';

interface IShortcutHandlers {
  disabled: boolean; // e.g. while a save is in flight
  undo: () => void;
  redo: () => void;
  zoom: (direction: 1 | -1) => void;
  clearPage: () => void;
  hasSelection: boolean;
  eraseSelected: () => void;
  selectTool: () => void;
}

/**
 * The editor's shortcuts, inert inside a text field so the browser's own
 * editing keys keep working:
 *   Ctrl/Cmd+Z undo · Ctrl/Cmd+Shift+Z / Ctrl/Cmd+Y redo
 *   +/- zoom the DOCUMENT, without a modifier: Ctrl/Cmd +/- belongs to the
 *   browser and no page can take it back, so the zoom keys are bare
 *   Ctrl/Cmd+Backspace/Delete clears the page · plain Backspace/Delete
 *   erases the selection · V picks the select tool (not Escape — Base UI's
 *   dialog owns that).
 */
export function useDrawingShortcuts({
  disabled, undo, redo, zoom, clearPage, hasSelection, eraseSelected, selectTool,
}: IShortcutHandlers): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (disabled) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const key = e.key.toLowerCase();
      const withMod = e.metaKey || e.ctrlKey;

      if (!withMod) {
        if (key === 'v') {
          e.preventDefault();
          selectTool();
        } else if (key === '+' || key === '=') {
          e.preventDefault();
          zoom(1);
        } else if (key === '-' || key === '_') {
          e.preventDefault();
          zoom(-1);
        } else if ((key === 'backspace' || key === 'delete') && hasSelection) {
          e.preventDefault();
          eraseSelected();
        }
        return;
      }

      if (key === 'z' || key === 'y') {
        e.preventDefault();
        if (key === 'y' || e.shiftKey) redo();
        else undo();
      } else if (key === 'backspace' || key === 'delete') {
        e.preventDefault();
        clearPage();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });
}